/**
 * BasicInfoForm Component - Collects company name, role, Census 2022 NAICS
 * industry, description, and domain before discovery.
 *
 * Version History:
 * v3.00 - 2026-10-28 - ONBOARDING-INDUSTRY-PICKER (Branko M31077): the 12
 *   hardcoded industry chip values and the free-text "industry" field are
 *   retired. The customer now picks their industry from three linked
 *   dropdowns built from the Census 2022 NAICS tree (sector required,
 *   subsector + industry group optional), followed by the IndustryMatchPicker
 *   popup that lists ranked 6-digit industry titles under the deepest level
 *   chosen. The customer never sees or types a code number. On Continue the
 *   popup opens, the pick becomes the 6-digit naicsCode submitted to
 *   basic-info, and the sector title is derived server-side from the same
 *   list.
 * v2.65 - 2026-03-07 - DEMO-01 round 3: read new campaign-code keys from
 *   sessionStorage, pre-fill company name.
 * v2.64 - 2026-03-06 - DEMO-01: read demo_ref/demo_campaign from
 *   sessionStorage, include in POST, clear after success.
 * v2.62 - 2026-02-05 - Fixed auth: use api service instead of raw fetch for
 *   Clerk token.
 * v2.53 - 2026-02-03 - Initial implementation.
 */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import CategoryIcon from '@mui/icons-material/Category';
import DescriptionIcon from '@mui/icons-material/Description';
import LanguageIcon from '@mui/icons-material/Language';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import api from '../services/api';
import IndustryMatchPicker, { IndustryMatch } from './IndustryMatchPicker';

// Role options (unchanged from v2.65; this is a form-local list).
const ROLES = [
  { value: 'owner_ceo', label: 'Owner / CEO' },
  { value: 'cfo_finance', label: 'CFO / Finance' },
  { value: 'coo_operations', label: 'COO / Operations' },
  { value: 'cto_it', label: 'CTO / IT' },
  { value: 'sales_marketing', label: 'Sales / Marketing' },
  { value: 'hr', label: 'HR / People' },
  { value: 'other', label: 'Other' },
];

interface IndustryTreeSixDigit {
  code: string;
  title: string;
}
interface IndustryTreeNationalIndustry {
  code: string;
  title: string;
  six_digit: IndustryTreeSixDigit[];
}
interface IndustryTreeGroup {
  code: string;
  title: string;
  national_industries: IndustryTreeNationalIndustry[];
}
interface IndustryTreeSubsector {
  code: string;
  title: string;
  industry_groups: IndustryTreeGroup[];
}
interface IndustryTreeSector {
  code: string;
  title: string;
  subsectors: IndustryTreeSubsector[];
}
interface IndustryTree {
  edition?: string;
  sectors: IndustryTreeSector[];
}

interface BasicInfoFormProps {
  onComplete: (data: {
    companyName: string;
    role: string;
    naicsCode: string;
    sector: string;
    businessDescription: string;
    companyDomain?: string;
  }) => void;
  onSkip?: () => void;
}

const BasicInfoForm: React.FC<BasicInfoFormProps> = ({ onComplete, onSkip }) => {
  const [companyName, setCompanyName] = useState(sessionStorage.getItem('demo_company_name') || '');
  const [role, setRole] = useState('');
  const [sectorCode, setSectorCode] = useState('');
  const [subsectorCode, setSubsectorCode] = useState('');
  const [groupCode, setGroupCode] = useState('');
  const [businessDescription, setBusinessDescription] = useState('');
  const [companyDomain, setCompanyDomain] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [tree, setTree] = useState<IndustryTree | null>(null);
  const [treeError, setTreeError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Load the Census 2022 NAICS tree once when the form mounts. The response
  // contains titles only (no code numbers surface to the customer).
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const response = await api.get('/onboarding/industries');
        if (!cancelled) {
          setTree(response?.data || null);
        }
      } catch (err: any) {
        if (!cancelled) {
          setTreeError(err?.message || 'Could not load the industry list. Please refresh.');
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const sectors = tree?.sectors || [];
  const sectorNode = sectors.find((s) => s.code === sectorCode) || null;
  const subsectors = sectorNode?.subsectors || [];
  const subsectorNode = subsectors.find((s) => s.code === subsectorCode) || null;
  const groups = subsectorNode?.industry_groups || [];
  const deepestCode = groupCode || subsectorCode || sectorCode;

  const descriptionLength = businessDescription.trim().length;
  const canOpenPicker =
    companyName.trim().length >= 2 &&
    Boolean(role) &&
    Boolean(sectorCode) &&
    descriptionLength >= 50 &&
    Boolean(deepestCode);

  const handleSectorChange = (value: string) => {
    setSectorCode(value);
    setSubsectorCode('');
    setGroupCode('');
  };
  const handleSubsectorChange = (value: string) => {
    setSubsectorCode(value);
    setGroupCode('');
  };

  const submitWithPickedCode = async (match: IndustryMatch) => {
    setIsSubmitting(true);
    setError(null);
    try {
      const demo_ref = sessionStorage.getItem('demo_ref') || undefined;
      const demo_campaign = sessionStorage.getItem('demo_campaign') || undefined;
      const demo_code = sessionStorage.getItem('demo_code') || undefined;
      const demo_source = sessionStorage.getItem('demo_source') || undefined;
      const demo_campaign_name = sessionStorage.getItem('demo_campaign_name') || undefined;
      const demo_email = sessionStorage.getItem('demo_email') || undefined;
      const is_demo = Boolean(demo_code || demo_ref);

      const response = await api.post('/onboarding/basic-info', {
        companyName: companyName.trim(),
        role,
        naicsCode: match.code,
        businessDescription: businessDescription.trim(),
        ...(companyDomain.trim() ? { companyDomain: companyDomain.trim() } : {}),
        ...(is_demo
          ? { is_demo, demo_ref, demo_campaign, demo_code, demo_source, demo_campaign_name, demo_email }
          : {}),
      });

      ['demo_ref', 'demo_campaign', 'demo_code', 'demo_source', 'demo_campaign_name', 'demo_email', 'demo_company_name'].forEach(
        (k) => sessionStorage.removeItem(k)
      );

      const data = response?.data || {};
      onComplete({
        companyName: companyName.trim(),
        role,
        naicsCode: data.naicsCode || match.code,
        sector: data.sector || '',
        businessDescription: businessDescription.trim(),
        ...(companyDomain.trim() ? { companyDomain: companyDomain.trim() } : {}),
      });
    } catch (err: any) {
      console.error('Failed to save basic info:', err);
      setError(err?.message || 'Failed to save. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContinue = () => {
    if (!canOpenPicker) return;
    setError(null);
    setPickerOpen(true);
  };

  const handlePickerPick = (match: IndustryMatch) => {
    setPickerOpen(false);
    submitWithPickedCode(match);
  };

  const handlePickerNoneFit = () => {
    setPickerOpen(false);
  };

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', py: 4 }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
          Let's Get Started
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Tell us a bit about your business so we can personalize your experience
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {treeError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {treeError}
        </Alert>
      )}

      {/* Form Fields */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Company Name */}
        <TextField
          fullWidth
          label="Company Name"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          placeholder="Enter your company name"
          InputProps={{
            startAdornment: <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
          helperText="This will be displayed throughout the platform"
        />

        {/* Role */}
        <FormControl fullWidth>
          <InputLabel>Your Role</InputLabel>
          <Select
            value={role}
            label="Your Role"
            onChange={(e) => setRole(e.target.value)}
            startAdornment={<PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />}
          >
            {ROLES.map((r) => (
              <MenuItem key={r.value} value={r.value}>
                {r.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Industry — three linked dropdowns from the Census 2022 NAICS tree */}
        <FormControl fullWidth disabled={!tree && !treeError}>
          <InputLabel>Industry</InputLabel>
          <Select
            value={sectorCode}
            label="Industry"
            onChange={(e) => handleSectorChange(e.target.value as string)}
            startAdornment={<CategoryIcon sx={{ mr: 1, color: 'text.secondary' }} />}
          >
            {sectors.map((s) => (
              <MenuItem key={s.code} value={s.code}>
                {s.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth disabled={!sectorCode || subsectors.length === 0}>
          <InputLabel>Subsector (optional)</InputLabel>
          <Select
            value={subsectorCode}
            label="Subsector (optional)"
            onChange={(e) => handleSubsectorChange(e.target.value as string)}
          >
            {subsectors.map((s) => (
              <MenuItem key={s.code} value={s.code}>
                {s.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth disabled={!subsectorCode || groups.length === 0}>
          <InputLabel>Industry group (optional)</InputLabel>
          <Select
            value={groupCode}
            label="Industry group (optional)"
            onChange={(e) => setGroupCode(e.target.value as string)}
          >
            {groups.map((g) => (
              <MenuItem key={g.code} value={g.code}>
                {g.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Business Description */}
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Describe your business"
          value={businessDescription}
          onChange={(e) => setBusinessDescription(e.target.value)}
          placeholder="Describe what your company makes or sells, who your customers are, and where you operate…"
          InputProps={{
            startAdornment: (
              <DescriptionIcon sx={{ mr: 1, mt: 1, color: 'text.secondary', alignSelf: 'flex-start' }} />
            ),
          }}
          helperText={
            descriptionLength < 50
              ? `${Math.max(0, 50 - descriptionLength)} more characters needed (helps us personalise your dashboard)`
              : 'Great, this helps us understand your industry and data needs'
          }
          error={businessDescription.length > 0 && descriptionLength < 50}
        />

        {/* Company Domain (optional) */}
        <TextField
          fullWidth
          label="Company website (optional)"
          value={companyDomain}
          onChange={(e) => setCompanyDomain(e.target.value)}
          placeholder="yourcompany.com"
          InputProps={{
            startAdornment: <LanguageIcon sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
          helperText="Helps us identify your industry. No www or https needed."
        />
      </Box>

      {/* Actions */}
      <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleContinue}
          disabled={!canOpenPicker || isSubmitting}
          endIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <ArrowForwardIcon />}
          sx={{ py: 1.5 }}
        >
          {isSubmitting ? 'Saving...' : 'Continue'}
        </Button>

        {onSkip && (
          <Button
            variant="text"
            color="inherit"
            onClick={onSkip}
            disabled={isSubmitting}
            sx={{ color: 'text.secondary' }}
          >
            Skip for now
          </Button>
        )}
      </Box>

      {/* Info text */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', textAlign: 'center', mt: 3 }}
      >
        You can update this information anytime in Settings
      </Typography>

      <IndustryMatchPicker
        open={pickerOpen}
        deepestCode={deepestCode}
        description={businessDescription.trim()}
        onPick={handlePickerPick}
        onNoneFit={handlePickerNoneFit}
        onCancel={handlePickerNoneFit}
      />
    </Box>
  );
};

export default BasicInfoForm;
